/**
 * Hooks para gestión del perfil de usuario
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  updatePassword,
  reauthenticateWithCredential,
  EmailAuthProvider,
  deleteUser,
} from "firebase/auth";
import {
  doc,
  updateDoc,
  serverTimestamp,
  collection,
  query,
  where,
  getDocs,
  writeBatch,
} from "firebase/firestore";
import { auth, db } from "@/lib/firebase";
import { useAuthStore } from "@/stores/authStore";
import { toast } from "react-hot-toast";
import { userKeys } from "@/hooks/useUsers";
import { personService } from "@/lib/services/persons";

interface UpdateProfileData {
  displayName: string;
  phone?: string;
}

interface ChangePasswordData {
  currentPassword: string;
  newPassword: string;
}

/**
 * Hook para actualizar el perfil del usuario actual
 */
export function useUpdateProfile() {
  const queryClient = useQueryClient();
  const { profile, setProfile } = useAuthStore();

  return useMutation({
    mutationFn: async (data: UpdateProfileData) => {
      if (!profile?.id) {
        throw new Error("No hay usuario autenticado");
      }

      const userRef = doc(db, "users", profile.id);

      const updateData = {
        displayName: data.displayName,
        phone: data.phone || null,
        updatedAt: serverTimestamp(),
      };

      await updateDoc(userRef, updateData);

      // Actualizar el perfil en el store
      setProfile({
        ...profile,
        displayName: data.displayName,
        phone: data.phone || undefined,
      });

      return { success: true };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.all });
    },
    onError: (error: Error) => {
      console.error("Error updating profile:", error);
      throw error;
    },
  });
}

/**
 * Hook para cambiar la contraseña del usuario actual
 */
export function useChangePassword() {
  const { profile, logout } = useAuthStore();

  return useMutation({
    mutationFn: async (data: ChangePasswordData) => {
      const currentUser = auth.currentUser;

      if (!currentUser || !currentUser.email) {
        throw new Error("No hay usuario autenticado");
      }

      // Re-autenticar antes de cambiar contraseña
      const credential = EmailAuthProvider.credential(
        currentUser.email,
        data.currentPassword,
      );

      try {
        await reauthenticateWithCredential(currentUser, credential);
      } catch (error) {
        const authError = error as { code?: string; message: string };
        if (
          authError.code === "auth/wrong-password" ||
          authError.code === "auth/invalid-credential"
        ) {
          throw {
            code: "auth/wrong-password",
            message: "Contraseña actual incorrecta",
          };
        }
        throw error;
      }

      // Cambiar la contraseña
      await updatePassword(currentUser, data.newPassword);

      // Actualizar el flag en Firestore si existe
      if (profile?.id) {
        const userRef = doc(db, "users", profile.id);
        await updateDoc(userRef, {
          requiresPasswordChange: false,
          updatedAt: serverTimestamp(),
        });
      }

      return { success: true };
    },
    onSuccess: () => {
      toast.success(
        "Contraseña actualizada. Por favor inicia sesión nuevamente.",
        {
          duration: 5000,
        },
      );
      // Cerrar sesión después de cambiar contraseña
      setTimeout(() => {
        logout();
      }, 2000);
    },
    onError: (error: Error) => {
      console.error("Error changing password:", error);
      throw error;
    },
  });
}

/**
 * Hook para verificar si el usuario necesita cambiar su contraseña
 * @deprecated El nuevo modelo de autenticación no usa este flag
 */
export function useRequiresPasswordChange(): boolean {
  // En el nuevo modelo con email/password, no hay contraseña temporal
  return false;
}

/**
 * BUG-003: Eliminar cuenta propia.
 *
 * Flujo:
 *  1. Re-autentica con la contraseña actual.
 *  2. Verifica que el usuario NO sea director de un club activo (bloquea si lo es).
 *  3. Anonimiza el doc users/{uid} (mantiene el doc para integridad de auditorías).
 *  4. Anonimiza persons/{personId} si existe (mantiene el doc para no romper members
 *     históricos en otros clubes / progreso histórico).
 *  5. Desvincula memberships activas: quita linkedUserId. (No las elimina:
 *     el director del club puede usar BUG-002 para eliminarlas si corresponde).
 *  6. Borra la cuenta de Firebase Auth.
 *  7. Logout.
 *
 * Limitaciones conocidas:
 *  - No borra la cuenta auth de OTROS usuarios (solo el current user).
 *  - No borra ClassProgress / Achievements: el director del club los gestiona.
 */
export function useDeleteAccount() {
  const queryClient = useQueryClient();
  const { profile, logout } = useAuthStore();

  return useMutation({
    mutationFn: async (data: { currentPassword: string }) => {
      const currentUser = auth.currentUser;
      if (!currentUser || !currentUser.email) {
        throw new Error("No hay usuario autenticado");
      }
      if (!profile?.id) {
        throw new Error("Perfil no cargado");
      }

      // 1) Re-autenticar
      const credential = EmailAuthProvider.credential(
        currentUser.email,
        data.currentPassword,
      );
      try {
        await reauthenticateWithCredential(currentUser, credential);
      } catch (error) {
        const authError = error as { code?: string; message: string };
        if (
          authError.code === "auth/wrong-password" ||
          authError.code === "auth/invalid-credential"
        ) {
          throw new Error("Contraseña incorrecta");
        }
        throw error;
      }

      // 2) Bloqueo si es director de CUALQUIER club (independiente del status)
      try {
        const directedSnap = await getDocs(
          query(
            collection(db, "clubs"),
            where("directorId", "==", profile.personId || "__none__"),
          ),
        );
        if (!directedSnap.empty) {
          const names = directedSnap.docs
            .map((d) => d.data()?.name || d.id)
            .join(", ");
          throw new Error(
            `No puedes eliminar tu cuenta porque eres director de: ${names}. Transfiere la dirección o cierra el club antes de continuar.`,
          );
        }
      } catch (e) {
        // Si el error es el de bloqueo, re-lanzar; otro error de lectura → continuar
        const msg = (e as Error).message || "";
        if (msg.startsWith("No puedes eliminar tu cuenta")) throw e;
        console.warn("No se pudo verificar dirección de clubes:", e);
      }

      const uid = profile.id;
      const personId = profile.personId;
      const anonEmail = `deleted-${uid}@deleted.local`;

      // ORDEN CRÍTICO: todas las operaciones que requieren isActiveUser()
      // deben ocurrir ANTES de marcar status="deleted" en users/{uid}.

      // 3) Anonimizar persons/{personId}
      if (personId) {
        try {
          const personRef = doc(db, "persons", personId);
          await updateDoc(personRef, {
            email: anonEmail,
            phone: null,
            address: null,
            healthInsurance: null,
            emergencyContactName: null,
            emergencyContactPhone: null,
            photoUrl: null,
            deletedBySelf: true,
            deletedAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          });
        } catch (e) {
          console.warn("No se pudo anonimizar persons:", e);
        }
      }

      // 4) Desvincular memberships activas (linkedUserId = uid)
      try {
        const membersSnap = await getDocs(
          query(collection(db, "members"), where("linkedUserId", "==", uid)),
        );
        if (!membersSnap.empty) {
          const docs = membersSnap.docs;
          const CHUNK = 450;
          for (let i = 0; i < docs.length; i += CHUNK) {
            const batch = writeBatch(db);
            for (const d of docs.slice(i, i + CHUNK)) {
              batch.update(d.ref, { linkedUserId: null });
            }
            await batch.commit();
          }
        }
      } catch (e) {
        console.warn("No se pudo desvincular memberships:", e);
      }

      // 5) Cancelar joinRequests pendientes del usuario
      try {
        const jrSnap = await getDocs(
          query(
            collection(db, "joinRequests"),
            where("userId", "==", uid),
            where("status", "==", "pending"),
          ),
        );
        if (!jrSnap.empty) {
          const batch = writeBatch(db);
          for (const d of jrSnap.docs) {
            batch.update(d.ref, {
              status: "cancelled",
              cancelledReason: "account_deleted",
              updatedAt: serverTimestamp(),
            });
          }
          await batch.commit();
        }
      } catch (e) {
        console.warn("No se pudieron cancelar joinRequests:", e);
      }

      // 6) Cancelar clubApprovalRequests pendientes
      try {
        const carSnap = await getDocs(
          query(
            collection(db, "clubApprovalRequests"),
            where("userId", "==", uid),
            where("status", "==", "pending"),
          ),
        );
        if (!carSnap.empty) {
          const batch = writeBatch(db);
          for (const d of carSnap.docs) {
            batch.update(d.ref, {
              status: "rejected",
              rejectionReason: "account_deleted",
              updatedAt: serverTimestamp(),
            });
          }
          await batch.commit();
        }
      } catch (e) {
        console.warn("No se pudieron cancelar clubApprovalRequests:", e);
      }

      // 7) Borrar fcmTokens del usuario (subcolección users/{uid}/fcmTokens)
      try {
        const fcmSnap = await getDocs(
          collection(db, "users", uid, "fcmTokens"),
        );
        if (!fcmSnap.empty) {
          const batch = writeBatch(db);
          for (const d of fcmSnap.docs) batch.delete(d.ref);
          await batch.commit();
        }
      } catch (e) {
        console.warn("No se pudieron borrar fcmTokens:", e);
      }

      // 8) Anonimizar users/{uid} (ÚLTIMO update Firestore: marca status="deleted"
      //    y rompe isActiveUser()).
      const userRef = doc(db, "users", uid);
      await updateDoc(userRef, {
        status: "deleted",
        deletedAt: serverTimestamp(),
        deletedBySelf: true,
        email: anonEmail,
        displayName: "Usuario eliminado",
        firstName: null,
        lastName: null,
        phone: null,
        photoUrl: null,
        updatedAt: serverTimestamp(),
      });

      // 9) Borrar cuenta de Firebase Auth.
      //    Si esto falla con auth/requires-recent-login, los datos ya quedaron
      //    anonimizados; el usuario debe contactar soporte para borrar Auth.
      try {
        await deleteUser(currentUser);
      } catch (e) {
        const authError = e as { code?: string };
        if (authError.code === "auth/requires-recent-login") {
          throw new Error(
            "Sesión expirada. Tus datos ya fueron anonimizados pero la cuenta de acceso no se pudo borrar. Por favor contacta a soporte.",
          );
        }
        throw e;
      }

      return { success: true };
    },
    onSuccess: () => {
      queryClient.clear();
      toast.success("Tu cuenta fue eliminada correctamente.", {
        duration: 5000,
      });
      // Logout para limpiar el store y redirigir
      setTimeout(() => {
        logout();
      }, 1500);
    },
    onError: (error: Error) => {
      console.error("Error eliminando cuenta:", error);
      toast.error(error.message || "Error al eliminar la cuenta");
    },
  });
}

/**
 * Hook para actualizar la foto de perfil del usuario actual
 */
export function useUpdateProfilePhoto() {
  const queryClient = useQueryClient();
  const { profile, setProfile } = useAuthStore();

  return useMutation({
    mutationFn: async (photoUrl: string | null) => {
      if (!profile?.id) {
        throw new Error("No hay usuario autenticado");
      }

      const userRef = doc(db, "users", profile.id);

      await updateDoc(userRef, {
        photoUrl: photoUrl,
        updatedAt: serverTimestamp(),
      });

      // Propagar la nueva foto al Person vinculado y a sus Members,
      // para que directores y asociación vean la URL actualizada de inmediato
      // (sin esperar al siguiente login del usuario).
      if (photoUrl) {
        try {
          await personService.syncUserPhoto(profile.id, photoUrl);
        } catch (err) {
          console.warn(
            "[useUpdateProfilePhoto] syncUserPhoto falló (continuando):",
            err,
          );
        }
      }

      // Actualizar el perfil en el store
      setProfile({
        ...profile,
        photoUrl: photoUrl || undefined,
      });

      return { success: true };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.all });
    },
    onError: (error: Error) => {
      console.error("Error updating profile photo:", error);
      throw error;
    },
  });
}
